import { ChangeDetectorRef, Component, forwardRef, Input, OnInit } from '@angular/core';
import {
  AbstractControl, ControlValueAccessor, UntypedFormControl, UntypedFormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR,
  ValidationErrors, Validator
} from '@angular/forms';
import { takeUntil } from 'rxjs/operators';

import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';

export interface RangeOrRelativeDateInputModel {
  relativeOffset?: number;
  /**ISO Formatted */
  startDate?: string;
  /**ISO Formatted */
  endDate?: string;
}
/**
 * Displays input that allows user to select a relative time frame.
 * Output and input are in seconds.
 */
@Component({
  selector: 'app-range-or-relative-date-input',
  templateUrl: `./range-or-relative-date-input.component.html`,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => RangeOrRelativeDateInputComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => RangeOrRelativeDateInputComponent), multi: true }
  ]
})
export class RangeOrRelativeDateInputComponent extends WithDisposableSubject()
  implements OnInit, ControlValueAccessor, Validator {

  static checkModelHasValue(model: RangeOrRelativeDateInputModel): boolean {
    return !!(model?.endDate || model?.startDate || model?.relativeOffset);
  }

  range = new UntypedFormGroup({
    start: new UntypedFormControl(),
    end: new UntypedFormControl(),
  });

  /** Disabled state of the input */
  @Input() disabled: boolean;

  /** Set the background of the material inputs */
  @Input() inputTheme: 'white' | 'gray' = 'gray';

  datePickerType: 'range' | 'relative';

  relativeOffset: number;

  get hasDateRange() {
    const rv = this.range.value;
    return rv.start || rv.end;
  }

  // For ControlValueAccessor interface
  public onChangeCallback: () => void;
  public onTouchedCallback: () => any;

  constructor(private _cd: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.range.valueChanges
      .pipe(
        takeUntil(this.disposer)
      )
      .subscribe(v => {
        this.handleDataChange();
      });
  }

  // ----------Validator Implementation----------
  validate(control: AbstractControl): ValidationErrors | null {
    return null;
  }

  // ----------ControlValueAccessor Implementation----------
  get value(): RangeOrRelativeDateInputModel {
    const rv = this.range.value;

    return {
      startDate: rv.start,
      endDate: rv.end,
      relativeOffset: this.relativeOffset
    };
  }

  writeValue(obj: any): void {
    if (obj) {
      this.relativeOffset = obj.relativeOffset;
      if (obj.relativeOffset != null) {
        this.datePickerType = 'relative';
      } else {
        this.datePickerType = 'range';
      }
      this.range.setValue({
        start: obj.startDate ? new Date(obj.startDate) : null,
        end: obj.endDate ? new Date(obj.endDate) : null
      });
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = () => fn(this.value);
  }
  registerOnTouched(fn: any): void {
    this.onTouchedCallback = () => fn(this.value);
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._cd.detectChanges();
  }

  // ----------END----------


  setDatePickerType(type: 'range' | 'relative') {
    if (this.datePickerType === type) {
      return;
    }
    this.datePickerType = type;

    if (type === 'relative') {
      this.range.reset();
    }

    if (type === 'range') {
      this.relativeOffset = null;
      this.handleDataChange();
    }

  }

  onRelativeOffsetChanged(event: number) {
    this.relativeOffset = event;
    this.handleDataChange();
  }

  clearDate() {
    this.range.reset();
  }

  setNullOnEmpty(event: any, controlName: string) {
    if (event.target?.value === '') {
      this.range.get(controlName).setValue(null);
    }
  }

  private handleDataChange(): void {
    this._cd.markForCheck();
    if (this.onChangeCallback) {
      this.onChangeCallback();
    }
  }
}
